import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { TabPanel, Tabs } from '../ui/Tabs';
import { PeriodBar } from '../sales/PeriodBar';
import { usePeriodParams } from '../sales/usePeriodParams';
import { useCan } from '../useCan';
import { API_BASE } from '../../core/api/http';
import type { Permission } from '../../core/types/team';
import { OverviewTab } from './tabs/OverviewTab';
import { CustomersTab } from './tabs/CustomersTab';
import { SalonTab } from './tabs/SalonTab';
import { WebshopTab } from './tabs/WebshopTab';
import { MarginTab } from './tabs/MarginTab';
import { SourcesTab } from './tabs/SourcesTab';
import { ReachTab } from './reach/ReachTab';

// Klanten (met namen) en de omzet per behandeling of product: alleen met die bevoegdheid. Marketing
// ziet overzicht, bereik en bronnen (zelfde regel als de server, core/rules/endpointPermissions.ts).
const allTabs = [{
  key: 'overzicht',
  label: 'Overzicht'
}, {
  key: 'klanten',
  label: 'Klanten',
  permission: 'customers.view'
}, {
  key: 'salon',
  label: 'Salon',
  permission: 'revenue.view'
}, {
  key: 'webshop',
  label: 'Webshop',
  permission: 'revenue.view'
}, {
  key: 'marge',
  label: 'Marge',
  permission: 'finance.margin'
}, {
  key: 'bereik',
  label: 'Bereik'
}, {
  key: 'bronnen',
  label: 'Bronnen'
}] as const satisfies readonly {
  key: string;
  label: string;
  permission?: Permission;
}[];
type TabKey = (typeof allTabs)[number]['key'];

/**
 * Verkoop › Inzicht (docs/INZICHT.md): wat werkt, zonder cookies en zonder personen te volgen.
 * Tabblad en periode staan in het adres; de periode is dezelfde als bij Omzet.
 */
export function Insights() {
  const {
    canAll
  } = useCan();
  const tabs = allTabs.filter(t => !('permission' in t) || canAll(t.permission));
  const [params, setParams] = useSearchParams();
  const tab: TabKey = tabs.find(t => t.key === params.get('tab'))?.key ?? 'overzicht';
  const period = usePeriodParams();
  const setTab = (next: TabKey) => setParams(prev => {
    const p = new URLSearchParams(prev);
    if (next === 'overzicht') p.delete('tab');else p.set('tab', next);
    p.delete('bereik');
    return p;
  }, {
    replace: true
  });
  return <div data-id="element-1851" data-mp="1eywx3gqj6pcj">
      <PageHeader title="Inzicht" description={`Wat werkt, en wat je anders kunt doen. Zonder cookies; we tellen bezoek, we volgen niemand.${API_BASE === null ? ' Voorbeeldcijfers uit de demo.' : ''}`} data-id="element-1852" data-mp="1ezotuuvv6t4e" />
      <PeriodBar state={period} data-id="element-1853" data-mp="mddufnjzuy96" />
      <Tabs id="inzicht" label="Inzicht" tabs={tabs} value={tab} onChange={setTab} className="mb-6" data-id="element-1854" data-mp="hczj9wn3j86x" />
      <TabPanel id="inzicht" value={tab} data-id="element-1855" data-mp="vcqwhv5sgkjv">
        {tab === 'overzicht' && <OverviewTab period={period} data-id="element-1856" data-mp="v9wba1eyavpe" />}
        {tab === 'klanten' && <CustomersTab period={period} data-id="element-1857" data-mp="r9uea5k5uqgg" />}
        {tab === 'salon' && <SalonTab period={period} data-id="element-1858" data-mp="26quxvmephayy" />}
        {tab === 'webshop' && <WebshopTab period={period} data-id="element-1859" data-mp="26th4njtrvmg4" />}
        {tab === 'marge' && <MarginTab period={period} data-id="element-1860" data-mp="22or8ruz467v7" />}
        {tab === 'bereik' && <ReachTab period={period} data-id="element-1861" data-mp="2a9blxo12febd" />}
        {tab === 'bronnen' && <SourcesTab period={period} data-id="element-1862" data-mp="2a7qqzxau9tcz" />}
      </TabPanel>
    </div>;
}